import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'

/**
 * TeacherQualificationsChart (Bare) - Chart only, no wrapper
 * For embedding in narrative HTML templates
 */
function TeacherQualificationsChart({ data }) {
  const chartData = useMemo(() => {
    if (!data || typeof data !== 'object') return []

    const qualifications = [
      { key: 'degree', name: 'Degree Holders', color: '#10B981' },
      { key: 'diploma', name: 'Diploma Holders', color: '#3B82F6' },
      { key: 'certificate', name: 'Certificate Holders', color: '#F59E0B' },
    ]

    return qualifications
      .map(qual => ({
        name: qual.name,
        value: data[qual.key] || 0,
        color: qual.color,
        key: qual.key,
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value)
  }, [data])

  const total = useMemo(() => data?.total || 0, [data])

  const stackedBarOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: params => {
          let result = '<strong>Teacher Qualifications</strong><br/>'
          params.forEach(param => {
            const percentage = ((param.value / total) * 100).toFixed(1)
            result += `${param.seriesName}: ${param.value} (${percentage}%)<br/>`
          })
          return result
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        max: total,
      },
      yAxis: {
        type: 'category',
        data: ['Qualification Distribution'],
      },
      series: chartData.map(item => ({
        name: item.name,
        type: 'bar',
        stack: 'total',
        label: {
          show: true,
          formatter: params => {
            const percentage = ((params.value / total) * 100).toFixed(1)
            return `${params.value} (${percentage}%)`
          },
        },
        data: [item.value],
        itemStyle: {
          color: item.color,
        },
      })),
    }),
    [chartData, total]
  )

  if (!data || total === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No teacher qualification data available
      </div>
    )
  }

  return <ReactECharts option={stackedBarOptions} style={{ height: '200px' }} />
}

TeacherQualificationsChart.propTypes = {
  data: PropTypes.shape({
    total: PropTypes.number,
    degree: PropTypes.number,
    diploma: PropTypes.number,
    certificate: PropTypes.number,
  }),
}

export default TeacherQualificationsChart
